
import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { ChartContainer, ChartTooltip, ChartTooltipContent } from "@/components/ui/chart";
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, LineChart, Line } from "recharts";
import { ArrowUpRight, ArrowDownRight, Users, Briefcase, Clock, TrendingUp } from 'lucide-react';

interface AnalyticsSummary {
  applicationsOverTime: {
    date: string;
    count: number;
  }[];
  jobPerformance: {
    title: string;
    applicants: number;
  }[];
  qualityOfCandidates: {
    score: string;
    count: number;
  }[];
  timeToHire: {
    month: string;
    days: number;
  }[];
  conversionRate: number;
  applicationCompletionRate: number;
  averageTimeToHire: number;
  totalActiveJobs: number;
}

const AnalyticsOverview = ({ 
  stats, 
  analytics 
}: { 
  stats: {
    totalCandidates: number;
    newCandidates: number;
    shortlisted: number;
    reviewed: number;
    rejected: number;
    averageScore: number;
  };
  analytics: AnalyticsSummary;
}) => {
  const applicationChartConfig = {
    applications: {
      label: "Applications",
      color: "#4f46e5",
    },
  };

  const jobsChartConfig = {
    applicants: {
      label: "Applicants",
      color: "#10b981",
    },
  };

  const scoreChartConfig = {
    count: {
      label: "Candidates",
      color: "#f59e0b",
    },
  };

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {/* Active Jobs */}
        <Card>
          <CardContent className="p-6">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Active Jobs</p>
                <h3 className="text-3xl font-bold">{analytics.totalActiveJobs}</h3>
              </div>
              <div className="p-2 bg-blue-50 rounded-full text-blue-600">
                <Briefcase size={20} />
              </div>
            </div>
            <div className="mt-4 flex items-center text-sm">
              <div className="text-green-600 flex items-center">
                <ArrowUpRight size={14} className="mr-1" />
                <span className="font-medium">12%</span>
              </div>
              <span className="text-muted-foreground ml-1">from last month</span>
            </div>
          </CardContent>
        </Card>

        {/* Application Completion Rate */}
        <Card>
          <CardContent className="p-6">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Completion Rate</p>
                <h3 className="text-3xl font-bold">{analytics.applicationCompletionRate}%</h3>
              </div>
              <div className="p-2 bg-green-50 rounded-full text-green-600">
                <TrendingUp size={20} />
              </div>
            </div>
            <div className="mt-4 flex items-center text-sm">
              <div className="text-green-600 flex items-center">
                <ArrowUpRight size={14} className="mr-1" />
                <span className="font-medium">8%</span>
              </div>
              <span className="text-muted-foreground ml-1">from last month</span>
            </div>
          </CardContent>
        </Card>

        {/* Conversion Rate */}
        <Card>
          <CardContent className="p-6">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Conversion Rate</p>
                <h3 className="text-3xl font-bold">{analytics.conversionRate}%</h3>
              </div>
              <div className="p-2 bg-purple-50 rounded-full text-purple-600">
                <Users size={20} />
              </div>
            </div>
            <div className="mt-4 flex items-center text-sm">
              <div className="text-red-600 flex items-center">
                <ArrowDownRight size={14} className="mr-1" />
                <span className="font-medium">3%</span>
              </div>
              <span className="text-muted-foreground ml-1">from last month</span>
            </div>
          </CardContent>
        </Card>

        {/* Time to Hire */}
        <Card>
          <CardContent className="p-6">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Avg Time to Hire</p>
                <h3 className="text-3xl font-bold">{analytics.averageTimeToHire} days</h3>
              </div>
              <div className="p-2 bg-amber-50 rounded-full text-amber-600">
                <Clock size={20} />
              </div>
            </div>
            <div className="mt-4 flex items-center text-sm">
              <div className="text-green-600 flex items-center">
                <ArrowUpRight size={14} className="mr-1" />
                <span className="font-medium">5%</span>
              </div>
              <span className="text-muted-foreground ml-1">improvement</span>
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Applications Over Time */}
        <Card>
          <CardHeader>
            <CardTitle>Applications Over Time</CardTitle>
          </CardHeader>
          <CardContent>
            <ChartContainer config={applicationChartConfig} className="h-[250px]">
              <LineChart data={analytics.applicationsOverTime}>
                <XAxis 
                  dataKey="date" 
                  axisLine={false}
                  tickLine={false}
                  tickMargin={10}
                  tick={{ fontSize: 12 }}
                />
                <YAxis 
                  axisLine={false}
                  tickLine={false}
                  tickMargin={10}
                  tick={{ fontSize: 12 }}
                />
                <ChartTooltip
                  content={<ChartTooltipContent />}
                />
                <Line
                  type="monotone"
                  dataKey="count"
                  stroke="var(--color-applications)"
                  strokeWidth={2}
                  dot={false}
                  activeDot={{ r: 6, fill: "var(--color-applications)" }}
                />
              </LineChart>
            </ChartContainer>
          </CardContent>
        </Card>

        {/* Job Performance */}
        <Card>
          <CardHeader>
            <CardTitle>Job Performance</CardTitle>
          </CardHeader>
          <CardContent>
            <ChartContainer config={jobsChartConfig} className="h-[250px]">
              <BarChart data={analytics.jobPerformance}>
                <XAxis 
                  dataKey="title" 
                  axisLine={false}
                  tickLine={false}
                  tickMargin={10}
                  tick={{ fontSize: 12 }}
                />
                <YAxis 
                  axisLine={false}
                  tickLine={false}
                  tickMargin={10}
                  tick={{ fontSize: 12 }}
                />
                <ChartTooltip
                  content={<ChartTooltipContent />}
                />
                <Bar 
                  dataKey="applicants" 
                  fill="var(--color-applicants)" 
                  radius={[4, 4, 0, 0]} 
                />
              </BarChart>
            </ChartContainer>
          </CardContent>
        </Card>
      </div>

      {/* Candidate Quality Distribution */}
      <Card>
        <CardHeader>
          <CardTitle>Candidate Quality Distribution</CardTitle>
        </CardHeader>
        <CardContent>
          <ChartContainer config={scoreChartConfig} className="h-[250px]">
            <BarChart data={analytics.qualityOfCandidates}>
              <XAxis 
                dataKey="score" 
                axisLine={false}
                tickLine={false}
                tickMargin={10}
                tick={{ fontSize: 12 }}
              />
              <YAxis 
                axisLine={false}
                tickLine={false}
                tickMargin={10}
                tick={{ fontSize: 12 }}
              />
              <ChartTooltip
                content={<ChartTooltipContent />}
              />
              <Bar 
                dataKey="count" 
                fill="var(--color-count)" 
                radius={[4, 4, 0, 0]} 
              />
            </BarChart>
          </ChartContainer>
        </CardContent>
      </Card>
    </div>
  );
};

export default AnalyticsOverview;
